import { ArrowDropDown, CheckCircle, EastRounded, SearchOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Collapse,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { grey, indigo } from "../../../../colors";
import useFilterOptions from "../../../../contexts/useFilterOptions";
import { ClarifyingQuestionsResponse, EnhancedTemplate } from "../../models";
import ClarifyingContainer from "./ClarifyingContainer";
import { readableHint } from "./types";

// Enhanced questions: the backend offers more-specific rephrasings of the query. Picking one
// (or completing the inline template) sends it immediately as the new query.
export default function EnhancedQuestions({
  clarification,
  answered,
  onSelect,
  onSkip,
  onSkipAndTurnOff,
}: {
  clarification: ClarifyingQuestionsResponse;
  answered: boolean;
  onSelect?: (prompt: string) => void;
  onSkip: () => void;
  onSkipAndTurnOff?: () => void;
}) {
  const prompts = clarification.enhanced_prompts ?? [];
  const assumptions = clarification.assumptions ?? [];
  const template = clarification.enhanced_template ?? null;

  return (
    <ClarifyingContainer reasoning={clarification.reasoning} answered={answered} onSkipAndTurnOff={onSkipAndTurnOff}>
      {template ? (
        <EnhancedTemplatePrompt template={template} answered={answered} onSubmit={onSelect} />
      ) : prompts.length > 0 ? (
        <Stack spacing={0.75}>
          {prompts.map((prompt, i) => (
            <ListItemButton
              key={i}
              disabled={answered}
              onClick={() => onSelect?.(prompt)}
              sx={{
                borderRadius: "12px",
                border: `1px solid ${indigo[100]}`,
                backgroundColor: "#fff",
                py: "0.75em",
                px: "1em",
                alignItems: "flex-start",
                gap: "0.5em",
                "&:hover": answered ? {} : { borderColor: indigo[300], backgroundColor: indigo[50] },
              }}
            >
              <EastRounded sx={{ fontSize: 18, color: indigo[500], mt: "2px", flexShrink: 0 }} />
              <ListItemText primary={prompt} primaryTypographyProps={{ variant: "body2", color: grey[800] }} />
            </ListItemButton>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color={grey[600]}>
          Couldn&apos;t generate more specific suggestions. You can continue below.
        </Typography>
      )}

      {assumptions.length > 0 && !answered && (
        <Box mt="1em" display="flex" flexWrap="wrap" alignItems="center" gap="0.4em">
          <Typography variant="caption" color={grey[600]}>
            If you skip, I&apos;ll assume:
          </Typography>
          {assumptions.map((a, i) => (
            <Chip key={i} label={a} size="small" variant="outlined" sx={{ borderColor: grey[300], color: grey[700] }} />
          ))}
        </Box>
      )}

      {!answered && (
        <Typography variant="caption" color={grey[600]} display="block" mt="1em">
          Pick a suggestion above, or just type a new question in the chat box to continue.
        </Typography>
      )}

      <Box display="flex" justifyContent="flex-end" mt="0.5em">
        {clarification.skip_available && !answered && (
          <Button variant="text" onClick={onSkip} sx={{ color: grey[600] }}>
            Skip &amp; answer anyway
          </Button>
        )}
      </Box>

      {answered && (
        <Box display="flex" alignItems="center" gap="0.4em" mt="1em" color={grey[600]}>
          <CheckCircle sx={{ fontSize: 16, color: indigo[500] }} />
          <Typography variant="caption">Refined question sent.</Typography>
        </Box>
      )}
    </ClarifyingContainer>
  );
}

// Enhanced-questions product-agnostic template: renders the rephrased sentence with a single
// inline slot. The slot is a facet-backed dropdown when the field_hint matches a known facet,
// otherwise a free-text input. On submit the placeholder is filled with the chosen value and the
// completed sentence is sent immediately as the new query.
function EnhancedTemplatePrompt({
  template,
  answered,
  onSubmit,
}: {
  template: EnhancedTemplate;
  answered: boolean;
  onSubmit?: (prompt: string) => void;
}) {
  const [value, setValue] = useState("");
  const { data: globalFilters } = useFilterOptions();
  const hint = readableHint(template.slot.field_hint);
  const token = `{${template.slot.field_hint}}`;

  // Resolve dropdown options from the facet cache when the slot's field_hint is facet-backed.
  const facetOptions = useMemo(() => {
    if (!globalFilters || !template.slot.field_hint) return null;
    const facet = globalFilters.common_filters?.find((f) => f.column_name === template.slot.field_hint);
    const values = facet?.filter_values;
    if (!values || values.length === 0) return null;
    return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [globalFilters, template.slot.field_hint]);

  // Split the sentence around the single placeholder token so we can render the slot inline.
  const [before, after] = useMemo(() => {
    const idx = template.text.indexOf(token);
    if (idx === -1) return [template.text, ""];
    return [template.text.slice(0, idx), template.text.slice(idx + token.length)];
  }, [template.text, token]);

  const filledSentence = useMemo(() => {
    const filled = value.trim() || `_${hint}_`;
    return template.text.replace(token, filled);
  }, [template.text, token, value, hint]);

  const canSubmit = !answered && value.trim().length > 0;

  return (
    <Stack spacing="0.75em">
      {/* The sentence with the inline slot rendered where the placeholder sits. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.4em",
          p: "0.75em 1em",
          borderRadius: "10px",
          border: `1px solid ${grey[200]}`,
          backgroundColor: "#fff",
        }}
      >
        {before && (
          <Typography variant="body1" color={grey[800]}>
            {before}
          </Typography>
        )}
        <Box sx={{ minWidth: 200, flex: "0 1 auto" }}>
          <TemplateSlotControl
            options={facetOptions}
            value={value}
            hint={hint}
            disabled={answered}
            onChange={setValue}
          />
        </Box>
        {after && (
          <Typography variant="body1" color={grey[800]}>
            {after}
          </Typography>
        )}
      </Box>

      {/* Live preview of the completed query so the user sees what will be sent. */}
      {value.trim() && (
        <Typography variant="caption" color={grey[600]}>
          Sends: {filledSentence}
        </Typography>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          disableElevation
          disabled={!canSubmit}
          endIcon={<EastRounded />}
          onClick={() => canSubmit && onSubmit?.(filledSentence)}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: indigo[500],
            "&:hover": { backgroundColor: indigo[600] },
          }}
        >
          Use this
        </Button>
      </Box>
    </Stack>
  );
}

// The inline slot control for an enhanced-questions template.
function TemplateSlotControl({
  options,
  value,
  hint,
  disabled,
  onChange,
}: {
  options: string[] | null;
  value: string;
  hint: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Shave 4px off the default small-input height so the inline slot sits tighter within the sentence.
  const slotInputSx = {
    "& .MuiOutlinedInput-input": { py: "6.5px" },
  };

  const filtered = useMemo(() => {
    if (!options) return [];
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(lower));
  }, [options, search]);

  // Free-text slot: no facet options available for this dimension.
  if (!options) {
    return (
      <TextField
        fullWidth
        size="small"
        disabled={disabled}
        placeholder={hint}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{
          ...slotInputSx,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[200] },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: indigo[300] },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: indigo[300], borderWidth: 1 },
          },
        }}
      />
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          placeholder={`Select a ${hint}`}
          value={open ? search : value}
          onClick={() => !disabled && setOpen(true)}
          onChange={(event) => {
            setSearch(event.target.value);
            if (!open) setOpen(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 20, color: indigo[500] }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!disabled) setOpen((prev) => !prev);
                  }}
                  sx={{ p: "0.15em" }}
                >
                  <ArrowDropDown
                    sx={{
                      color: grey[500],
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            ...slotInputSx,
            "& .MuiOutlinedInput-root": {
              borderRadius: open ? "8px 8px 0 0" : "8px",
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[200] },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: indigo[300] },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: indigo[300], borderWidth: 1 },
            },
          }}
        />
        <Collapse in={open && !disabled} sx={{ position: "absolute", zIndex: 5, width: "100%" }}>
          <Paper
            elevation={2}
            sx={{
              border: `1px solid ${grey[200]}`,
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              maxHeight: 220,
              overflowY: "auto",
              backgroundColor: "#fff",
            }}
          >
            {filtered.length === 0 && (
              <Box px="1em" py="0.75em">
                <Typography variant="body2" color={grey[500]}>
                  No options found
                </Typography>
              </Box>
            )}
            {filtered.map((option) => (
              <Box
                key={option}
                onClick={() => {
                  onChange(option);
                  setSearch("");
                  setOpen(false);
                }}
                sx={{
                  px: "0.75em",
                  py: "0.4em",
                  cursor: "pointer",
                  borderBottom: `1px solid ${grey[100]}`,
                  backgroundColor: option === value ? indigo[50] : "transparent",
                  "&:hover": { backgroundColor: indigo[50] },
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Typography variant="body2" color={grey[800]}>
                  {option}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
}
